import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateSocialHistoryBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/socialHistory/{id}
 *
 * Shared endpoint for Edit and soft-Delete on Social History. Wire body
 * curl-verified by Andrew 2026-05-20. See `UpdateSocialHistoryBody` type
 * doc for the round-trip rules (most notably: `createdBy` and
 * `performerDetails` round-trip from the ORIGINAL record, not the
 * editing user; `code.category` is nulled on Update; lowercase
 * `updatedby` wire field).
 *
 * Cache: invalidates `['patient-social-history', patientId]`. SignalR
 * `UpdatePatientChat` "SocialHistory" event also fires for concurrent
 * sessions.
 */

interface UpdateSocialHistoryParams {
  patientId: string;
  socialHistoryId: string;
  body: UpdateSocialHistoryBody;
}

export function useUpdateSocialHistory() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateSocialHistoryParams>({
    mutationFn: ({ socialHistoryId, body }) =>
      put<string>(`/api/Patients/socialHistory/${socialHistoryId}`, body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-social-history', patientId]);
    },
  });
}
